import React, { useState } from "react";
import {
  Eye, Trash2, X, Plus, Edit2, Upload, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, Loader2, BookOpen, Clock, Globe, EyeOff,
} from "lucide-react";
import {
  useGetAdminBlogsQuery,
  useCreateBlogMutation,
  useUpdateBlogMutation,
  useDeleteBlogMutation,
  useUploadImagesMutation,
} from "../store/api/adminApi";
import { useSelector } from "react-redux";
import { useToast } from "../components/Toast";

const PAGE_SIZE = 10;

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      {...props}
      className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
    />
  );
}

function CoverUpload({ url, onUpload, isUploading }) {
  const ref = React.useRef();

  const handleFiles = (files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("images", f));
    onUpload(fd);
  };

  return (
    <div
      onClick={() => !isUploading && ref.current.click()}
      className="w-full aspect-[16/9] bg-gray-100 rounded-xl flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all overflow-hidden relative"
    >
      {url ? (
        <img src={url} alt="cover" className="w-full h-full object-cover" />
      ) : (
        <div className="flex flex-col items-center gap-1.5">
          {isUploading ? (
            <Loader2 size={22} className="text-gray-400 animate-spin" />
          ) : (
            <Upload size={22} className="text-gray-400" strokeWidth={1.6} />
          )}
          <span className="text-[11px] font-manrope-regular text-gray-400 text-center px-4">
            {isUploading ? "Uploading…" : "Upload cover image"}
          </span>
        </div>
      )}
      {isUploading && url && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
          <Loader2 size={22} className="text-primary animate-spin" />
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

function BlogModal({ mode = "add", blog = null, onClose }) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    title:       isEdit ? blog?.title || "" : "",
    excerpt:     isEdit ? blog?.excerpt || "" : "",
    content:     isEdit ? blog?.content || "" : "",
    category:    isEdit ? blog?.category || "General" : "General",
    author:      isEdit ? blog?.author || "Contentour Bonaire" : "Contentour Bonaire",
    readTime:    isEdit ? String(blog?.readTime || "") : "",
    isPublished: isEdit ? !!blog?.isPublished : false,
  });
  const [coverImage, setCoverImage] = useState(isEdit ? blog?.coverImage || "" : "");
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const [createBlog,  { isLoading: isCreating }] = useCreateBlogMutation();
  const [updateBlog,  { isLoading: isUpdating }] = useUpdateBlogMutation();
  const [uploadImages, { isLoading: isUploading }] = useUploadImagesMutation();
  const toast = useToast();
  const isSaving = isCreating || isUpdating;

  const handleCoverUpload = async (formData) => {
    try {
      const res = await uploadImages(formData).unwrap();
      setCoverImage(res.data.images[0]?.url || "");
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error("Image upload failed: " + (err?.data?.error?.message || "Unknown error"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.content.trim()) {
      setError("Title and content are required.");
      return;
    }
    const body = {
      title:       form.title.trim(),
      excerpt:     form.excerpt.trim(),
      content:     form.content.trim(),
      category:    form.category.trim() || "General",
      author:      form.author.trim() || "Contentour Bonaire",
      coverImage:  coverImage || null,
      isPublished: form.isPublished,
      readTime:    form.readTime ? Number(form.readTime) : null,
    };
    try {
      if (isEdit) {
        await updateBlog({ id: blog._id || blog.id, ...body }).unwrap();
        toast.success("Blog post updated successfully.");
      } else {
        await createBlog(body).unwrap();
        toast.success("Blog post created successfully.");
      }
      onClose();
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to save blog post.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[800px] max-h-[92vh] overflow-y-auto flex">
        {/* Left – cover */}
        <div className="w-[220px] flex-shrink-0 p-5 border-r border-gray-100">
          <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wider mb-3">Cover image</p>
          <CoverUpload url={coverImage} onUpload={handleCoverUpload} isUploading={isUploading} />
          {coverImage && (
            <button
              type="button"
              onClick={() => setCoverImage("")}
              className="mt-2 text-[11px] text-red-400 hover:text-red-600 font-manrope-regular transition-all"
            >
              Remove cover
            </button>
          )}
        </div>

        {/* Right – form */}
        <form onSubmit={handleSubmit} className="flex-1 p-6 flex flex-col gap-0">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[16px] font-manrope-bold text-gray-900">
              {isEdit ? "Edit blog post" : "New blog post"}
            </h2>
            <button type="button" onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
              <X size={15} className="text-gray-500" />
            </button>
          </div>

          <div className="flex flex-col gap-3.5 flex-1">
            <Field label="Title *">
              <Input placeholder="e.g. Top 5 things to do in Bonaire" value={form.title} onChange={set("title")} />
            </Field>

            <Field label="Excerpt (short summary)">
              <textarea
                placeholder="A short summary shown in the blog card…"
                value={form.excerpt}
                onChange={set("excerpt")}
                rows={2}
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all resize-none"
              />
            </Field>

            <Field label="Content *">
              <textarea
                placeholder="Write the full article here…"
                value={form.content}
                onChange={set("content")}
                rows={8}
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all resize-y min-h-[120px]"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <Input placeholder="e.g. Travel, Tips, News" value={form.category} onChange={set("category")} />
              </Field>
              <Field label="Author">
                <Input placeholder="Author name" value={form.author} onChange={set("author")} />
              </Field>
            </div>

            <Field label="Read time (minutes)">
              <Input type="number" min="1" placeholder="e.g. 5" value={form.readTime} onChange={set("readTime")} />
            </Field>

            {/* Published toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={form.isPublished} onChange={set("isPublished")} className="sr-only peer" />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
              </label>
              <span className="text-[13px] font-manrope-regular text-gray-600">
                Published — visible to readers
              </span>
            </div>

            {error && (
              <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">{error}</p>
            )}

            <button
              type="submit"
              disabled={isSaving}
              className="mt-1 w-full py-3 bg-primary rounded-xl text-white text-[14px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
            >
              {isSaving && <Loader2 size={15} className="animate-spin" />}
              {isSaving ? "Saving…" : isEdit ? "Save changes" : "Publish post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteModal({ blog, onCancel, onConfirm, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">Delete blog post?</h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-1 leading-relaxed">
          You are about to delete{" "}
          <span className="font-manrope-semibold text-gray-700">"{blog?.title}"</span>.
          This action cannot be undone.
        </p>
        <div className="flex gap-3 w-full mt-5">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
          >
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BlogDetailsPanel({ blog, onClose, onEdit, onDelete }) {
  const formattedDate = blog?.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : blog?.createdAt
    ? new Date(blog.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="w-[300px] bg-white border-l border-gray-100 flex flex-col fixed right-0 top-0 h-full z-40 shadow-xl overflow-y-auto">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-[15px] font-manrope-bold text-gray-900">Post details</h2>
        <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all">
          <X size={15} className="text-gray-500" />
        </button>
      </div>

      <div className="flex-1 px-5 py-5 flex flex-col gap-4">
        {/* Cover */}
        <div className="w-full aspect-[16/10] rounded-xl overflow-hidden bg-gray-100">
          {blog.coverImage ? (
            <img src={blog.coverImage} alt="cover" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <BookOpen size={32} strokeWidth={1.2} />
            </div>
          )}
        </div>

        {/* Title + category */}
        <div>
          <h3 className="text-[14px] font-manrope-bold text-gray-900 leading-snug">{blog.title}</h3>
          <span className="text-[11px] font-manrope-regular text-primary bg-primary/10 px-2 py-0.5 rounded-full inline-block mt-1">
            {blog.category}
          </span>
        </div>

        {/* Excerpt */}
        {blog.excerpt && (
          <p className="text-[12px] font-manrope-regular text-gray-500 leading-relaxed">{blog.excerpt}</p>
        )}

        {/* Meta */}
        <div className="flex gap-3">
          {blog.readTime && (
            <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-0.5">
                <Clock size={10} className="text-gray-400" strokeWidth={1.6} />
                <p className="text-[10px] font-manrope-regular text-gray-400">Read time</p>
              </div>
              <p className="text-[14px] font-manrope-bold text-gray-900">{blog.readTime} min</p>
            </div>
          )}
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] font-manrope-regular text-gray-400 mb-0.5">Author</p>
            <p className="text-[12px] font-manrope-bold text-gray-900 truncate">{blog.author}</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl px-3 py-2 text-[12px] font-manrope-regular text-gray-600">
          <span className="font-manrope-semibold">Date:</span> {formattedDate}
        </div>

        {/* Published badge */}
        <div className={`text-center py-1.5 rounded-xl text-[11px] font-manrope-semibold ${blog.isPublished ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
          {blog.isPublished ? "Published" : "Draft"}
        </div>

        {/* Content preview */}
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-[10px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-1.5">Content preview</p>
          <p className="text-[12px] font-manrope-regular text-gray-600 leading-relaxed line-clamp-6 whitespace-pre-wrap">
            {blog.content}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <button onClick={onEdit} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-700 hover:bg-gray-50 transition-all">
            Edit
          </button>
          <button onClick={onDelete} className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 transition-all">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Blogs() {
  const user = useSelector((s) => s.auth.user);
  const [search, setSearch] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [modal, setModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isError } = useGetAdminBlogsQuery();
  const [deleteBlog, { isLoading: isDeleting }] = useDeleteBlogMutation();
  const toast = useToast();

  const blogs = data?.data || [];
  const filtered = blogs.filter((b) => b.title?.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleDelete = async () => {
    try {
      await deleteBlog(deleteTarget._id || deleteTarget.id).unwrap();
      setDeleteTarget(null);
      if (selectedBlog?.id === deleteTarget.id) setSelectedBlog(null);
      toast.success("Blog post deleted.");
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to delete blog post.");
    }
  };

  return (
    <>
      <div className={`flex flex-col min-h-screen transition-all ${selectedBlog ? "mr-[300px]" : ""}`}>
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">Blog management</h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              Create and manage blog posts shown on the booking platform.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-manrope-bold text-gray-600">{user?.name?.[0]?.toUpperCase() || "A"}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">{user?.name}</span>
              <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">{user?.email}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 px-8 pb-8">
          <div className="flex items-center gap-3 mb-5">
            <input
              type="text"
              placeholder="Search blog posts"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full max-w-xs pl-4 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
            />
            <button
              onClick={() => setModal("add")}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary rounded-xl text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all ml-auto"
            >
              <Plus size={15} strokeWidth={2.2} />
              New post
            </button>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[2.5fr_1fr_1fr_1fr] px-5 py-3 border-b border-gray-50">
              {["Title", "Category", "Status", "Action"].map((col) => (
                <span key={col} className="text-[12px] font-manrope-semibold text-gray-400">{col}</span>
              ))}
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[13px] font-manrope-regular">Loading posts…</span>
              </div>
            )}

            {isError && (
              <div className="flex items-center justify-center py-16 text-red-400 text-[13px] font-manrope-regular">
                Failed to load blog posts. Please try again.
              </div>
            )}

            {!isLoading && !isError && paginated.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                <BookOpen size={36} strokeWidth={1.2} />
                <span className="text-[13px] font-manrope-regular">No blog posts yet. Create your first one!</span>
              </div>
            )}

            {paginated.map((blog) => (
              <div
                key={blog._id}
                className="grid grid-cols-[2.5fr_1fr_1fr_1fr] px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-all items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    {blog.coverImage ? (
                      <img src={blog.coverImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <BookOpen size={14} className="text-gray-300" strokeWidth={1.4} />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[13px] font-manrope-semibold text-gray-800 leading-tight line-clamp-1">{blog.title}</span>
                    <span className="text-[11px] font-manrope-regular text-gray-400">{blog.author}</span>
                  </div>
                </div>

                <span className="text-[12px] font-manrope-regular text-gray-500">{blog.category}</span>

                <div className="flex items-center gap-1.5">
                  {blog.isPublished ? (
                    <>
                      <Globe size={11} className="text-green-500" strokeWidth={2} />
                      <span className="text-[12px] font-manrope-semibold text-green-600">Published</span>
                    </>
                  ) : (
                    <>
                      <EyeOff size={11} className="text-gray-400" strokeWidth={2} />
                      <span className="text-[12px] font-manrope-regular text-gray-400">Draft</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedBlog(blog)}
                    className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                  >
                    <Eye size={14} className="text-gray-500" strokeWidth={1.6} />
                  </button>
                  <button
                    onClick={() => { setSelectedBlog(blog); setModal("edit"); }}
                    className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                  >
                    <Edit2 size={13} className="text-gray-500" strokeWidth={1.6} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(blog)}
                    className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-all"
                  >
                    <Trash2 size={14} className="text-primary" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination */}
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-[12px] font-manrope-regular text-gray-500">
                Total <span className="font-manrope-bold text-gray-800">{filtered.length}</span> posts
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all">
                  <ChevronsLeft size={14} />
                </button>
                <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all">
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => { if (idx > 0 && arr[idx - 1] !== p - 1) acc.push("..."); acc.push(p); return acc; }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <span key={`el-${i}`} className="w-7 h-7 flex items-center justify-center text-[12px] text-gray-400">…</span>
                    ) : (
                      <button key={p} onClick={() => setCurrentPage(p)} className={`w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-manrope-semibold transition-all ${currentPage === p ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-100"}`}>{p}</button>
                    )
                  )}
                <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all">
                  <ChevronRight size={14} />
                </button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all">
                  <ChevronsRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedBlog && (
        <BlogDetailsPanel
          blog={selectedBlog}
          onClose={() => setSelectedBlog(null)}
          onEdit={() => setModal("edit")}
          onDelete={() => setDeleteTarget(selectedBlog)}
        />
      )}

      {modal && (
        <BlogModal
          mode={modal}
          blog={selectedBlog}
          onClose={() => { setModal(null); if (modal === "add") setSelectedBlog(null); }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          blog={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
